import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CustomerLimitBanner from '../components/CustomerLimitBanner';
import { UserContext } from '../context/UserContext';
import { Role } from '@redpanda/types';
import { Button, UserManagementTable, btnSecondary } from '@redpanda/ui';
import { formatTableDate } from '@redpanda/utils';
import { getApprovedUsers, getPendingUsers, updateUserRole, deleteUser, approveUser, rejectUser } from '../services/api';
import { isSubscriptionEnabled } from '../utils/environment';

// 使用者管理表（@redpanda/ui 共用，R26）呼叫的 API
const userManagementApi = { getApprovedUsers, getPendingUsers, updateUserRole, deleteUser, approveUser, rejectUser };

const AdminPage: React.FC = () => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const userRole = userContext?.user?.role;
  const navigate = useNavigate();

  useEffect(() => {
    if (userRole !== Role.SUPER_ADMIN) {
      navigate('/');
    }
  }, [userRole, navigate]);

  if (userRole !== Role.SUPER_ADMIN) {
    return null;
  }

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">{t('adminPanel.title', { ns: 'pages' })}</h1>
      
      {/* 客戶數量使用情況面板 */}
      <CustomerLimitBanner showAlways={true} />
      
      {userRole === Role.SUPER_ADMIN && (
        <div className="mb-8">
          <h2 className="text-base font-semibold text-fg mb-3">{t('adminPanel.userManagement', { ns: 'pages' })}</h2>
          <UserManagementTable api={userManagementApi} currentUser={userContext?.user} formatDate={formatTableDate} />
        </div>
      )}

      {/* 兩個入口：一個容器＋細線分隔；按鈕都是次要（同一區塊不放兩顆主色） */}
      <div className="grid grid-cols-1 md:grid-cols-2 bg-surface rounded-lg border border-line divide-y md:divide-y-0 md:divide-x divide-line">
        <section className="p-5">
          <h2 className="text-base font-semibold text-fg mb-1">{t('adminPanel.dataManagementLinkTitle', { ns: 'pages' })}</h2>
          <p className="text-sm text-fg-secondary mb-4">
            {t('adminPanel.dataManagementLinkDescription', { ns: 'pages' })}
          </p>
          <Button variant="secondary" onClick={() => navigate('/data-io')}>
            {t('adminPanel.goToDataIO', { ns: 'pages' })}
          </Button>
        </section>

        {/* Subscription Management Section - Only show for non-Docker versions */}
        {isSubscriptionEnabled() && (
          <section className="p-5">
            <h2 className="text-base font-semibold text-fg mb-1">{t('adminPanel.subscriptionManagement', { ns: 'pages' })}</h2>
            <p className="text-sm text-fg-secondary mb-4">
              {t('adminPanel.subscriptionDescription', { ns: 'pages' })}
            </p>
            <a
              href={`${process.env.REACT_APP_ACCOUNT_PORTAL_URL || 'http://localhost:8003'}/crm`}
              target="_blank"
              rel="noopener noreferrer"
              className={btnSecondary}
            >
              {t('common.navigation.manageSubscription')}
            </a>
          </section>
        )}
      </div>
    </div>
  );
};

export default AdminPage;